import React from "react";
import { useDataQuery } from "@Libs/DataLayer/hooks/useDataQuery";
import { useLocation } from "@Libs/Router/hooks/useLocation";
import useI18n from "@I18n/hooks/useI18n";
import { getFeature } from "@Libs/FeatureFlags/services/features";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { IFAQFeature, IFAQQuestion } from "../services/faqFeature";
import messages from "../Components/FAQSection/messages";
import { localFAQs } from "../__fixtures__/localFAQs";

const pathNamesThatShouldReadFAQFromLocale = [
  "choosePlan",
  "chooseFormula",
  "close",
  "serviceFee",
  "subscribe",
  "service_requests",
];

export const useFAQQuestions = () => {
  const { localize, formatMessage } = useI18n();
  const { pathname } = useLocation();
  const isActivationRateEnabled = useFeatureFlag("new-activation-rate");

  let localizedQuestions: IFAQQuestion[] = [];

  // this will get FAQ from local rather than from getting from Feature Flag
  if (
    pathNamesThatShouldReadFAQFromLocale.some((pathName) =>
      new RegExp(pathName).test(pathname)
    )
  ) {
    const filteredQuestions = localFAQs.filter(({ paths }) =>
      paths.some((pathRegex) => new RegExp(pathRegex).test(pathname))
    );
    localizedQuestions = filteredQuestions.map(({ question, answer }) => {
      const resolvedAnswer =
        isActivationRateEnabled &&
        answer === "isThePriceOfThePackageIncludedInMyServiceProviderOfferAnswer"
          ? "isThePriceOfThePackageIncludedInMyServiceProviderOfferAnswerActivationRate"
          : answer;
      return {
        question: formatMessage(messages[question]),
        answer: formatMessage(messages[resolvedAnswer], {
          bold: (chunks) => React.createElement("strong", null, chunks),
        }),
      };
    });

    return { questions: localizedQuestions };
  }

  // get other FAQ from FAQ Feature Flag
  const { data: feature, status } = useDataQuery<IFAQFeature>(
    "faqFeature",
    () => getFeature<IFAQFeature>("faq").then(({ data }) => data)
  );

  const questions = feature?.details?.questions;

  if (questions) {
    const filteredQuestions = questions.filter(({ paths }) =>
      paths.some((pathRegex) => new RegExp(pathRegex).test(pathname))
    );

    localizedQuestions = filteredQuestions.map(({ question, answer }) => ({
      question: localize(question, false),
      answer: localize(answer, false),
    }));
  }

  return { questions: localizedQuestions, status };
};
